import { Children, isValidElement, type ReactElement, type ReactNode, useState } from "react";

// A fita de abas, sem nada sobre o que elas mostram.
//
// Havia duas implementações do mesmo botão: esta, nos guias, e a de dentro de
// src/openapi/Reference.tsx, para as linguagens do request e os status da
// resposta — mesmo estado, mesma classe `active`, mesmo laço. As duas continuam
// com aparências diferentes porque estão em lugares diferentes: nos guias a fita
// é o topo de um painel; na referência ela divide a linha com o método e o
// rótulo. Isso é `variant`, e é a única coisa que as separa.
//
// A seleção é por índice, e não por rótulo: dois status iguais ou dois <Tab> com
// o mesmo label existem, e por rótulo o segundo nunca seria selecionável.
interface TabListProps {
  labels: string[];
  active: number;
  onSelect: (index: number) => void;
  /** "panel": fita no topo de um bloco. "inline": dentro de um cabeçalho. */
  variant?: "panel" | "inline";
  /** `title` de cada botão, na ordem de `labels`. Na referência, a descrição do status. */
  titles?: (string | undefined)[];
}

export function TabList({ labels, active, onSelect, variant = "panel", titles }: TabListProps) {
  const base = variant === "inline" ? "ref-lang-tab" : "pg-tab";
  return (
    <div className={variant === "inline" ? "ref-lang-tabs" : "pg-tabs"}>
      {labels.map((label, i) => (
        <button
          key={`${i}-${label}`}
          type="button"
          className={`${base} ${active === i ? "active" : ""}`}
          onClick={() => onSelect(i)}
          title={titles?.[i]}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// <Tabs>
//   <Tab label="Node.js"> … </Tab>
//   <Tab label="Python">  … </Tab>
// </Tabs>
export function Tabs({ children }: { children?: ReactNode }) {
  const items = Children.toArray(children).filter((c): c is ReactElement<{ label?: string }> => isValidElement(c));
  const [active, setActive] = useState(0);
  if (!items.length) return null;

  const index = Math.min(active, items.length - 1);
  const labels = items.map((child, i) => child.props.label || `Tab ${i + 1}`);

  return (
    <div className="doc-tabs">
      <TabList labels={labels} active={index} onSelect={setActive} />
      <div className="doc-tabs-panel">{items[index]}</div>
    </div>
  );
}

export function Tab({ children }: { label?: string; children?: ReactNode }) {
  return <>{children}</>;
}

export default Tabs;
